import { OntologyRolodexEntryType } from 'features/dynamicOntology'
import { RolodexHistogramFilter } from 'types'

export enum MetricCalculationType {
  COUNT = 'count',
  SUM = 'sum',
  MIN = 'min',
  MAX = 'max',
  MEAN = 'mean',
}

export enum FieldOperator {
  ADD = 'add',
  SUBTRACT = 'subtract',
  MULTIPLY = 'multiply',
  DIVIDE = 'divide',
}

// Single source of truth for operator display labels and symbols.
// Using a Record so adding a new FieldOperator forces adding its metadata.
// `symbol` is the typographic character shown in the UI; `mathSymbol` is the
// operator understood by the mathjs expression parser.
export const FIELD_OPERATOR_META: Readonly<
  Record<FieldOperator, { label: string; symbol: string; mathSymbol: string }>
> = {
  [FieldOperator.ADD]: { label: 'Add (+)', symbol: '+', mathSymbol: '+' },
  [FieldOperator.SUBTRACT]: {
    label: 'Subtract (−)',
    symbol: '−',
    mathSymbol: '-',
  },
  [FieldOperator.MULTIPLY]: {
    label: 'Multiply (×)',
    symbol: '×',
    mathSymbol: '*',
  },
  [FieldOperator.DIVIDE]: {
    label: 'Divide (÷)',
    symbol: '÷',
    mathSymbol: '/',
  },
}

export enum ExpressionType {
  FIELD = 'field',
  GROUP = 'group',
  OPERATION = 'operation',
}
export type FieldExpression =
  | { type: ExpressionType.FIELD; field: string }
  | { type: ExpressionType.GROUP; expressions: ReadonlyArray<FieldExpression> }
  | { type: ExpressionType.OPERATION; operation: FieldOperator }

// Calculation types that don't require field expressions
export const CALCULATION_TYPES_WITHOUT_FIELDS = [
  MetricCalculationType.COUNT,
] as const

export type DrilldownMetricConfig = {
  metricType: string
  rolodexType: OntologyRolodexEntryType
  title: string
  // Calculation type (count, sum, min, max, mean)
  calculationType: MetricCalculationType
  // Field expressions for calculation (not needed for COUNT)
  fieldExpressions?: ReadonlyArray<FieldExpression>
  filters?: ReadonlyArray<RolodexHistogramFilter>
  suffix?: string
  numDecimals?: number
  description?: string
}

// TODO @choikh: The metrics panel configs should be pulled from explorer state.
// Note: More complex cross-entry calculations are not supported yet.
// ex. multiplying one field from one entry with another field from another entry.
export const INDICATION_METRIC_CONFIGS: ReadonlyArray<DrilldownMetricConfig> = [
  {
    metricType: 'indication_count',
    rolodexType: 'indication',
    title: 'Number of Indications',
    calculationType: MetricCalculationType.COUNT,
    description: 'Total count of indications in the current filter',
  },
  {
    // Example of simple field expression
    metricType: 'circumferential_length_sum',
    rolodexType: 'indication',
    title: 'Circ Length Sum',
    calculationType: MetricCalculationType.SUM,
    fieldExpressions: [
      {
        type: ExpressionType.FIELD,
        field: 'indication.selection.y.end.num',
      },
      {
        type: ExpressionType.OPERATION,
        operation: FieldOperator.SUBTRACT,
      },
      {
        type: ExpressionType.FIELD,
        field: 'indication.selection.y.start.num',
      },
    ],
    numDecimals: 3,
    description: 'Sum of all circumferential lengths (end - start)',
  },
  {
    // Example of complex operation with grouping
    metricType: 'indication_area_sum',
    rolodexType: 'indication',
    title: 'Indication Area Sum',
    calculationType: MetricCalculationType.SUM,
    fieldExpressions: [
      {
        type: ExpressionType.GROUP,
        expressions: [
          {
            type: ExpressionType.FIELD,
            field: 'indication.selection.y.end.num',
          },
          {
            type: ExpressionType.OPERATION,
            operation: FieldOperator.SUBTRACT,
          },
          {
            type: ExpressionType.FIELD,
            field: 'indication.selection.y.start.num',
          },
        ],
      },
      {
        type: ExpressionType.OPERATION,
        operation: FieldOperator.MULTIPLY,
      },
      {
        type: ExpressionType.GROUP,
        expressions: [
          {
            type: ExpressionType.FIELD,
            field: 'indication.selection.x.end.num',
          },
          {
            type: ExpressionType.OPERATION,
            operation: FieldOperator.SUBTRACT,
          },
          {
            type: ExpressionType.FIELD,
            field: 'indication.selection.x.start.num',
          },
        ],
      },
    ],
    numDecimals: 3,
    description: 'Sum of all indication areas (height * width)',
  },
]

export const DEFAULT_AVAILABLE_METRIC_CONFIGS: ReadonlyArray<DrilldownMetricConfig> =
  [...INDICATION_METRIC_CONFIGS]
